/**
 * Platform detection helper.
 *
 * Works in three contexts:
 *   1. Preview / web (no Capacitor installed) — every check returns false.
 *   2. iOS Capacitor wrapper — isIOS() / isNative() return true.
 *   3. Android Capacitor wrapper (future) — isAndroid() / isNative() return true.
 *
 * IMPORTANT: this file MUST NOT throw or break in environments where
 * `@capacitor/core` is not installed (i.e. the current Emergent preview
 * environment). The package is added to package.json only when the iOS
 * project is generated on a Mac, and at that point this helper picks it up
 * automatically. Until then, it falls back to safe `false` defaults so the
 * web app keeps shipping every PWA-only UI element as it does today.
 */

let _cap = null;
try {
  // Optional require so the web bundle doesn't fail when @capacitor/core
  // isn't installed yet. Webpack/CRA will tree-shake this away in production
  // builds if the module truly isn't present.
   
  _cap = require('@capacitor/core');
} catch {
  _cap = null;
}

function _platform() {
  try {
    return _cap?.Capacitor?.getPlatform?.() ?? 'web';
  } catch {
    return 'web';
  }
}

export function isNative() {
  try {
    return !!_cap?.Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

export function isIOS() {
  return _platform() === 'ios';
}

export function isAndroid() {
  return _platform() === 'android';
}

export function isWeb() {
  return _platform() === 'web';
}

// Convenience: when running inside the iOS App Store wrapper, we hide
// (a) the PWA "Add to Home Screen" banner, (b) the BetaList badge, and
// (c) Stripe subscription CTAs (Apple guideline 3.1.1).
export const shouldHidePWAPrompt = () => isNative();
export const shouldHideBetaListBadge = () => isNative();
export const shouldHideSubscriptionCTAs = () => isIOS();
